import { Modal } from "@/components/modal";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

interface ModalContextProps {
    isOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    setModalContent: (content: ReactNode) => void;
    setIconName: (content: string) => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [modalContent, setModalContent] = useState<ReactNode>(null);
    const [iconName, setIconName] = useState<string | undefined>(undefined);

    const openModal = () => setIsOpen(true);
    const closeModal = () => [setIsOpen(false), setIconName("")];

    const value = useMemo(
        () => ({
            isOpen,
            openModal,
            closeModal,
            setModalContent,
            setIconName,
        }),
        [isOpen, modalContent, iconName],
    );

    return (
        <ModalContext.Provider value={value}>
            {children}
            <Modal isOpen={isOpen} onClose={closeModal} iconName={iconName}>
                {modalContent}
            </Modal>
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);

    if (context === undefined) {
        throw new Error("useModal must be used within a ModalProvider");
    }
    return context;
};
